/**
 * CompsARVPage — /v3/intel/comps-arv
 *
 * V3-native surface for BP-004 Comparable Sales Intelligence. Thin
 * premium presentation layer over /api/v3/comps endpoints. No new
 * business logic.
 *
 * Layout:
 *   LEFT (wider):  Comps table — inline edit rows, add-manual, auto-search,
 *                  lock/exclude/adjust actions per comp
 *   RIGHT (side):  ARV Card — band (low / expected / high) + confidence ring,
 *                  Calculate/Publish CTAs, AI Review, PDF, ARV History
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronDown, Loader2, Save, Send, Sparkles, Trash2, Plus,
  ArrowUpRight, FileDown, TrendingUp, TrendingDown, Search,
  CheckCircle2, Lock, LockOpen, EyeOff, Eye, RefreshCw, Ruler,
  BadgeCheck, History, AlertTriangle,
} from "lucide-react";
import DailyBusinessScore from "@/pages/v3/DailyBusinessScore";
import useSelectedProperty from "@/hooks/useSelectedProperty";

// ─────────────────────────────────────────── constants ──────────────────

const CONDITION_META = {
  superior:  { label: "SUPERIOR",  color: "#10B981" },
  similar:   { label: "SIMILAR",   color: "#3B82F6" },
  inferior:  { label: "INFERIOR",  color: "#EA580C" },
  renovated: { label: "RENOVATED", color: "#8B5CF6" },
  unknown:   { label: "UNKNOWN",   color: "#94A3B8" },
};
const TREND_META = {
  up:      { label: "TRENDING UP",   icon: TrendingUp,   color: "#10B981" },
  flat:    { label: "FLAT MARKET",   icon: null,         color: "#F59E0B" },
  down:    { label: "COOLING",       icon: TrendingDown, color: "#EA580C" },
  unknown: { label: "UNKNOWN",       icon: null,         color: "#94A3B8" },
};

const DEFAULT_NEW_COMP = {
  address: "", sale_price: "", sale_date: new Date().toISOString().slice(0, 10),
  sqft: "", distance: "0.5", beds: "3", baths: "2",
  condition: "similar", adjustment: "0", notes: "",
};

// ─────────────────────────────────────────── helpers ────────────────────

function fmtMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}
function fmtMoneyFull(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `$${Math.round(v).toLocaleString()}`;
}
function timeAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

// ─────────────────────────────────────────── page ──────────────────────

export default function CompsARVPage() {
  const [properties, setProperties] = useState([]);
  const { propertyId, setPropertyId } = useSelectedProperty(properties);
  const [loadingProps, setLoadingProps] = useState(true);

  const [comps, setComps] = useState([]);
  const [publishedArv, setPublishedArv] = useState(null);
  const [latestArv, setLatestArv] = useState(null);
  const [history, setHistory] = useState([]);

  const [subjectSqft, setSubjectSqft] = useState("");
  const [notes, setNotes] = useState("");

  const [calculating, setCalculating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [searchDrawer, setSearchDrawer] = useState(false);

  const propertyMap = useMemo(() => {
    const m = {}; properties.forEach((p) => { m[p.id] = p; }); return m;
  }, [properties]);

  // Bootstrap properties.
  useEffect(() => {
    let alive = true;
    api.get("/v3/properties?limit=200").then(({ data }) => {
      if (!alive) return;
      const list = data?.items || [];
      setProperties(list);
    }).catch(() => toast.error("Couldn't load properties"))
      .finally(() => { if (alive) setLoadingProps(false); });
    return () => { alive = false; };
  }, []);

  // Load current comps + ARVs when property changes.
  const loadAll = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const [c, h] = await Promise.all([
        api.get(`/v3/properties/${pid}/comps`).then((r) => r.data),
        api.get(`/v3/properties/${pid}/comps/arv-history`).then((r) => r.data).catch(() => ({ items: [] })),
      ]);
      setComps(c?.comps || []);
      setPublishedArv(c?.published_arv || null);
      setLatestArv(c?.latest_arv || null);
      setHistory(h?.items || []);
      // Seed subject sqft from property.
      const p = propertyMap[pid];
      setSubjectSqft(p?.sqft ? String(p.sqft) : (c?.latest_arv?.subject_sqft ? String(c.latest_arv.subject_sqft) : ""));
    } catch {
      setComps([]); setPublishedArv(null); setLatestArv(null); setHistory([]);
    }
  }, [propertyMap]);
  useEffect(() => { loadAll(propertyId); }, [propertyId, loadAll]);

  // Optimistic mutations on comps.
  const patchLocal = (id, patch) =>
    setComps((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeLocal = (id) => setComps((rows) => rows.filter((r) => r.id !== id));

  const patchComp = useCallback(async (id, patch) => {
    const prev = comps.find((r) => r.id === id);
    patchLocal(id, patch);
    try {
      const { data } = await api.patch(`/v3/comps/${id}`, patch);
      patchLocal(id, data);
    } catch (err) {
      if (prev) patchLocal(id, prev);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed");
    }
  }, [comps]);

  const deleteComp = useCallback(async (id) => {
    if (!window.confirm("Delete this comp? This can't be undone.")) return;
    const prev = comps.find((r) => r.id === id);
    removeLocal(id);
    try {
      await api.delete(`/v3/comps/${id}`);
      toast.success("Comp deleted");
    } catch (err) {
      if (prev) setComps((rows) => [...rows, prev]);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Delete failed");
    }
  }, [comps]);

  const addManual = useCallback(async (payload) => {
    try {
      const { data } = await api.post(
        `/v3/properties/${propertyId}/comps`,
        payload,
      );
      setComps((rows) => [...rows, data]);
      setAddOpen(false);
      toast.success("Comp added");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail[0]?.msg || "Add failed"
        : "Add failed");
    }
  }, [propertyId]);

  const runSearch = useCallback(async (params) => {
    setSearching(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${propertyId}/comps/search`, params,
      );
      const added = Number(data?.comps_added || 0);
      if (added > 0) {
        // Merge newly-added rows only, deduping by id in case the backend
        // echoes the full comp set (see iter123 backend note).
        setComps((rows) => {
          const seen = new Set(rows.map((r) => r.id));
          const fresh = (data.comps || []).filter((c) => !seen.has(c.id));
          return [...rows, ...fresh];
        });
        toast.success(`Added ${added} comp${added === 1 ? "" : "s"} · ${data.candidates_found} candidates`);
      } else {
        toast(`No new comps found · ${data.candidates_found || 0} candidates`, { icon: "🔍" });
      }
      setSearchDrawer(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Search failed");
    } finally {
      setSearching(false);
    }
  }, [propertyId]);

  const calculate = useCallback(async () => {
    if (!propertyId) return;
    const activeCount = comps.filter((c) => !c.excluded).length;
    if (activeCount < 3) {
      toast.error("Need at least 3 non-excluded comps to compute ARV.");
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${propertyId}/comps/calculate-arv`,
        {
          subject_sqft: subjectSqft ? Number(subjectSqft) : null,
          notes: notes.trim() || null,
        },
      );
      setLatestArv(data);
      setNotes("");
      // Refresh history.
      const h = await api.get(`/v3/properties/${propertyId}/comps/arv-history`)
        .then((r) => r.data).catch(() => ({ items: [] }));
      setHistory(h?.items || []);
      toast.success(`ARV calculated · v${data.version} · ${fmtMoney(data.expected_arv)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Calculation failed");
    } finally {
      setCalculating(false);
    }
  }, [propertyId, subjectSqft, notes, comps]);

  const publish = useCallback(async () => {
    if (!latestArv?.id) { toast.error("Calculate an ARV first."); return; }
    if (latestArv.is_published) { toast("Already published"); return; }
    if (!window.confirm(
      `Publish ARV v${latestArv.version} at ${fmtMoneyFull(latestArv.expected_arv)}? This overwrites this property's estimated_arv.`,
    )) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/v3/comps/arv/${latestArv.id}/publish`);
      setLatestArv(data);
      setPublishedArv(data);
      // Refresh history.
      const h = await api.get(`/v3/properties/${propertyId}/comps/arv-history`)
        .then((r) => r.data).catch(() => ({ items: [] }));
      setHistory(h?.items || []);
      toast.success("Published — Property estimated_arv updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Publish failed");
    } finally {
      setPublishing(false);
    }
  }, [latestArv, propertyId]);

  const runAi = useCallback(async () => {
    if (aiLoading) return;
    if (!latestArv?.id) { toast.error("Calculate an ARV first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/comps/arv/${latestArv.id}/ai-review`);
      setLatestArv(data);
      toast.success("AI review complete");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "AI review failed");
    } finally {
      setAiLoading(false);
    }
  }, [latestArv, aiLoading]);

  const exportPdf = useCallback(() => {
    if (!latestArv?.id) { toast.error("Calculate an ARV first."); return; }
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/comps/arv/${latestArv.id}/export/pdf`,
      "_blank",
    );
  }, [latestArv]);

  const activeComps = comps.filter((c) => !c.excluded);
  const includedIds = new Set(latestArv?.included_comp_ids || []);

  return (
    <div className="space-y-6" data-testid="comps-arv-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
            Intelligence Engine
          </div>
          <h1 className="mt-1 text-[32px] font-bold text-slate-900 tracking-tight leading-tight">
            Comps &amp; ARV
          </h1>
          <p className="mt-2 text-slate-600 text-[14px] max-w-2xl">
            Weighted comparable sales analysis with locked comps, condition adjustments,
            and a publishable ARV band that flows into every other engine.
          </p>
        </div>
        <PropertyPicker
          properties={properties}
          value={propertyId}
          onChange={setPropertyId}
          loading={loadingProps}
        />
      </div>

      {/* Empty state */}
      {!loadingProps && properties.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-14 text-center shadow-sm"
             data-testid="comps-empty-no-props">
          <Ruler className="w-8 h-8 mx-auto mb-3 text-amber-500" />
          <div className="text-slate-900 font-bold text-lg">No properties yet</div>
          <div className="text-sm text-slate-500 mt-1 mb-5">
            Comps &amp; ARV runs on individual properties — add one first.
          </div>
          <Link to="/v3/crm/pipeline"
                className="inline-flex items-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-3.5 py-2 shadow-md hover:shadow-lg"
                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            Open Pipeline <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {propertyId && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(320px,1fr)] gap-4">
          {/* ─── COMPS TABLE ─── */}
          <section
            data-testid="comps-table-panel"
            className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
          >
            <div className="h-1 w-full" style={{ backgroundColor: "#F59E0B" }} />
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 flex-wrap">
              <div className="h-8 w-8 rounded-lg grid place-items-center text-white shadow-md shrink-0"
                   style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
                <Ruler className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600 leading-none">
                  Comparable Sales
                </div>
                <div className="mt-0.5 text-[15px] font-bold text-slate-900 leading-tight">
                  {activeComps.length} active · {comps.length - activeComps.length} excluded
                </div>
              </div>
              <button type="button"
                      onClick={() => setSearchDrawer(true)}
                      data-testid="comps-auto-search-btn"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[12.5px] font-semibold px-3 py-1.5">
                <Search className="w-4 h-4" /> Auto-search
              </button>
              <button type="button"
                      onClick={() => setAddOpen(true)}
                      data-testid="comps-add-btn"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-semibold px-3 py-1.5">
                <Plus className="w-4 h-4" /> Add comp
              </button>
            </div>

            {/* Header row */}
            {comps.length > 0 && (
              <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50/40 text-[9px] uppercase tracking-widest font-bold text-slate-500">
                <div className="col-span-3">Address</div>
                <div className="col-span-2 text-right">Sale price</div>
                <div className="col-span-1 text-right">$/sqft</div>
                <div className="col-span-1 text-right">Sqft</div>
                <div className="col-span-1 text-right">Dist</div>
                <div className="col-span-1">Cond</div>
                <div className="col-span-2 text-right">Adjustment</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>
            )}

            {/* Rows */}
            <div className="divide-y divide-slate-100" data-testid="comps-list">
              {comps.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="mx-auto h-12 w-12 rounded-xl grid place-items-center bg-amber-50 border border-amber-200 mb-3">
                    <Ruler className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="text-slate-900 font-bold">No comps yet</div>
                  <div className="text-sm text-slate-500 mt-1 mb-4">
                    Run an auto-search or add a comp manually to get started.
                  </div>
                  <div className="inline-flex gap-2">
                    <button type="button"
                            data-testid="comps-auto-search-empty"
                            onClick={() => setSearchDrawer(true)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-semibold px-3 py-2">
                      <Search className="w-4 h-4" /> Auto-search comps
                    </button>
                    <button type="button"
                            data-testid="comps-add-empty"
                            onClick={() => setAddOpen(true)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[12.5px] font-semibold px-3 py-2">
                      <Plus className="w-4 h-4" /> Add manually
                    </button>
                  </div>
                </div>
              ) : (
                comps.map((c) => (
                  <CompRow
                    key={c.id} comp={c}
                    included={includedIds.has(c.id)}
                    onPatch={(patch) => patchComp(c.id, patch)}
                    onDelete={() => deleteComp(c.id)}
                  />
                ))
              )}
            </div>
          </section>

          {/* ─── ARV CARD + SIDE PANELS ─── */}
          <div className="space-y-4 self-start lg:sticky lg:top-4">
            {/* ARV Card */}
            <section
              data-testid="comps-arv-card"
              className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
            >
              <div className="h-1 w-full"
                   style={{ backgroundColor: latestArv?.is_published ? "#10B981" : "#F59E0B" }} />
              <div className="p-5">
                <div className="flex items-center gap-3">
                  <DailyBusinessScore
                    score={latestArv?.confidence_score ?? 0}
                    size="sm"
                    label="Confidence"
                    testid="comps-arv-confidence-ring"
                  />
                  <div className="min-w-0 flex-1">
                    {latestArv?.is_published ? (
                      <div
                        data-testid="comps-arv-published-chip"
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ring-1 bg-emerald-50 text-emerald-700 border-emerald-200"
                      >
                        <BadgeCheck className="w-3 h-3" />
                        PUBLISHED · v{latestArv.version}
                      </div>
                    ) : latestArv ? (
                      <div
                        data-testid="comps-arv-draft-chip"
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ring-1 bg-amber-50 text-amber-700 border-amber-200"
                      >
                        DRAFT · v{latestArv.version}
                      </div>
                    ) : (
                      <div className="inline-flex text-[10px] uppercase tracking-widest font-black text-slate-400">
                        NOT CALCULATED YET
                      </div>
                    )}
                    <div className="mt-1.5 text-[26px] font-bold text-slate-900 tabular-nums leading-none"
                         data-testid="comps-arv-expected">
                      {latestArv ? fmtMoneyFull(latestArv.expected_arv) : "—"}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Expected ARV
                    </div>
                  </div>
                </div>

                {/* ARV band */}
                {latestArv && (
                  <div className="mt-4 space-y-1.5" data-testid="comps-arv-band">
                    <ARVBandBar
                      low={Number(latestArv.low_arv)}
                      expected={Number(latestArv.expected_arv)}
                      high={Number(latestArv.high_arv)}
                    />
                    <div className="grid grid-cols-3 gap-2 text-[11px]">
                      <div className="text-slate-500">
                        Low <div className="text-slate-900 font-bold tabular-nums" data-testid="comps-arv-low">{fmtMoney(latestArv.low_arv)}</div>
                      </div>
                      <div className="text-center text-slate-500">
                        Expected <div className="text-amber-600 font-bold tabular-nums">{fmtMoney(latestArv.expected_arv)}</div>
                      </div>
                      <div className="text-right text-slate-500">
                        High <div className="text-slate-900 font-bold tabular-nums" data-testid="comps-arv-high">{fmtMoney(latestArv.high_arv)}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* PPSF */}
                {latestArv && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <KpiTile label="Avg $/sqft" color="#3B82F6"
                             value={`$${Number(latestArv.avg_ppsf || 0).toFixed(2)}`}
                             testid="comps-arv-avgPpsf" />
                    <KpiTile label="Weighted $/sqft" color="#F59E0B"
                             value={`$${Number(latestArv.weighted_ppsf || 0).toFixed(2)}`}
                             testid="comps-arv-weightedPpsf" />
                  </div>
                )}

                {/* Market trend */}
                {latestArv?.market_trend && latestArv.market_trend !== "unknown" && (() => {
                  const t = TREND_META[latestArv.market_trend];
                  const Icon = t.icon;
                  return (
                    <div className="mt-3 rounded-lg border px-3 py-1.5 flex items-center gap-2 text-[11.5px] font-semibold"
                         style={{ borderColor: `${t.color}40`, backgroundColor: `${t.color}10`, color: t.color }}
                         data-testid="comps-arv-market-trend">
                      {Icon && <Icon className="w-3.5 h-3.5" />}
                      {t.label}
                    </div>
                  );
                })()}

                {/* Warnings */}
                {latestArv?.warnings?.length > 0 && (
                  <div className="mt-3 space-y-1" data-testid="comps-arv-warnings">
                    {latestArv.warnings.map((w, i) => (
                      <div key={i} className="text-[11px] text-slate-700 flex items-start gap-1.5"
                           data-testid={`comps-arv-warn-${w.code || i}`}>
                        <AlertTriangle className="w-3 h-3 text-amber-600 mt-0.5 shrink-0" />
                        <span>{w.message || String(w)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Calculate row */}
              <div className="border-t border-slate-100 p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <FormGroup label="Subject sqft">
                    <input
                      type="number" min="0" value={subjectSqft}
                      onChange={(e) => setSubjectSqft(e.target.value)}
                      data-testid="comps-subject-sqft"
                      className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </FormGroup>
                  <FormGroup label="Notes (optional)">
                    <input
                      type="text" value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="e.g. Post-inspection"
                      data-testid="comps-notes"
                      className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </FormGroup>
                </div>
                <div className="flex gap-2">
                  <button type="button"
                          onClick={calculate}
                          disabled={calculating || publishing}
                          data-testid="comps-calculate-btn"
                          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-semibold px-3 py-2 disabled:opacity-60">
                    {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Calculate ARV
                  </button>
                  <button type="button"
                          onClick={publish}
                          disabled={publishing || !latestArv || latestArv.is_published}
                          data-testid="comps-publish-btn"
                          className="inline-flex items-center gap-1.5 rounded-lg text-white text-[12.5px] font-semibold px-3 py-2 shadow-md disabled:opacity-60"
                          style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
                    {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {latestArv?.is_published ? "Published" : "Publish"}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button type="button"
                          onClick={runAi}
                          disabled={aiLoading || !latestArv}
                          data-testid="comps-ai-btn"
                          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[12px] font-semibold px-2 py-1.5 disabled:opacity-60">
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    AI Review
                  </button>
                  <button type="button"
                          onClick={exportPdf}
                          disabled={!latestArv}
                          data-testid="comps-pdf-btn"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[12px] font-semibold px-2 py-1.5 disabled:opacity-60">
                    <FileDown className="w-4 h-4" /> PDF
                  </button>
                </div>
              </div>

              {latestArv?.is_published && (
                <div className="border-t border-slate-100 px-4 py-2.5 bg-emerald-50/60 flex items-center gap-2 text-[11.5px] text-emerald-800"
                     data-testid="comps-published-banner">
                  <CheckCircle2 className="w-4 h-4" />
                  Property estimated_arv is now live.
                </div>
              )}
            </section>

            {/* AI review */}
            {latestArv?.ai_review && (
              <section
                data-testid="comps-ai-panel"
                className="rounded-2xl border border-amber-200 bg-amber-50/40 shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-amber-200/70 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">
                    AI Review · Claude Sonnet 4.6
                  </div>
                </div>
                <div className="p-4 space-y-3 text-[12.5px] text-slate-800 leading-relaxed">
                  {latestArv.ai_review.summary && (
                    <p data-testid="comps-ai-summary" className="whitespace-pre-line">
                      {latestArv.ai_review.summary}
                    </p>
                  )}
                  {latestArv.ai_review.confidenceCommentary && (
                    <div className="rounded-lg bg-white/70 border border-amber-100 px-3 py-2 text-[11.5px] italic text-slate-700"
                         data-testid="comps-ai-confidence">
                      “{latestArv.ai_review.confidenceCommentary}”
                    </div>
                  )}
                  {Array.isArray(latestArv.ai_review.strongComps) && latestArv.ai_review.strongComps.length > 0 && (
                    <AiList label="Strong comps" testid="comps-ai-strong"
                            items={latestArv.ai_review.strongComps} color="#10B981" />
                  )}
                  {Array.isArray(latestArv.ai_review.weakComps) && latestArv.ai_review.weakComps.length > 0 && (
                    <AiList label="Weak comps" testid="comps-ai-weak"
                            items={latestArv.ai_review.weakComps} color="#EA580C" />
                  )}
                  {Array.isArray(latestArv.ai_review.outliers) && latestArv.ai_review.outliers.length > 0 && (
                    <AiList label="Outliers" testid="comps-ai-outliers"
                            items={latestArv.ai_review.outliers} color="#DC2626" />
                  )}
                  {Array.isArray(latestArv.ai_review.missingData) && latestArv.ai_review.missingData.length > 0 && (
                    <AiList label="Missing data" testid="comps-ai-missing"
                            items={latestArv.ai_review.missingData} color="#94A3B8" />
                  )}
                  {latestArv.ai_review.recommendMoreComps && (
                    <div className="rounded-lg bg-amber-100/70 border border-amber-200 px-3 py-2 text-[11.5px] font-semibold text-amber-800 flex items-center gap-1.5"
                         data-testid="comps-ai-recommend-more">
                      <Search className="w-3.5 h-3.5" /> Consider pulling more comps for a tighter band.
                    </div>
                  )}
                  {latestArv.ai_review.disclaimer && (
                    <div className="text-[10.5px] text-slate-500 italic border-t border-amber-100 pt-2"
                         data-testid="comps-ai-disclaimer">
                      {latestArv.ai_review.disclaimer}
                    </div>
                  )}
                  {/* Legacy 'flags' shape fallback */}
                  {Array.isArray(latestArv.ai_review.flags) && latestArv.ai_review.flags.length > 0 && (
                    <ul className="mt-2 space-y-1 text-[11.5px]">
                      {latestArv.ai_review.flags.map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <AlertTriangle className="w-3 h-3 text-amber-600 mt-0.5 shrink-0" />
                          <span>{typeof f === "string" ? f : f.message || JSON.stringify(f)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            )}

            {/* ARV history */}
            {history.length > 0 && (
              <section
                data-testid="comps-arv-history-panel"
                className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" />
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    ARV history ({history.length})
                  </div>
                </div>
                <div className="max-h-[240px] overflow-y-auto divide-y divide-slate-50">
                  {history.slice(0, 12).map((h) => (
                    <div key={h.id}
                         data-testid={`comps-arv-history-${h.id}`}
                         className="px-3 py-2 hover:bg-amber-50/40 transition-colors">
                      <div className="flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 rounded-full ${h.is_published ? "bg-emerald-500" : "bg-amber-500"}`} />
                        <div className="text-[12px] font-bold text-slate-900 tabular-nums flex-1">
                          {fmtMoney(h.expected_arv)}
                        </div>
                        <div className="text-[10px] text-slate-500 tabular-nums">
                          v{h.version}
                        </div>
                      </div>
                      <div className="text-[10.5px] text-slate-500 mt-0.5 pl-3">
                        Confidence {h.confidence_score} · {timeAgo(h.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {addOpen && (
        <AddCompDialog onSubmit={addManual} onClose={() => setAddOpen(false)} />
      )}
      {searchDrawer && (
        <SearchDrawer
          searching={searching}
          onSubmit={runSearch}
          onClose={() => setSearchDrawer(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────── ARV band bar ─────────────

function ARVBandBar({ low, expected, high }) {
  const range = Math.max(high - low, 1);
  const pos = Math.max(0, Math.min(1, (expected - low) / range));
  return (
    <div className="relative h-3 rounded-full overflow-hidden bg-slate-100">
      <div className="absolute inset-0 rounded-full"
           style={{ backgroundImage: "linear-gradient(90deg, #EA580C, #F59E0B, #10B981)" }} />
      <div
        className="absolute top-0 h-full w-1 bg-slate-900 shadow-md"
        style={{ left: `calc(${pos * 100}% - 2px)` }}
        title="Expected ARV"
      />
    </div>
  );
}

// ─────────────────────────────────────────── CompRow ────────────

function CompRow({ comp, included, onPatch, onDelete }) {
  const [addr, setAddr] = useState(comp.address);
  const [price, setPrice] = useState(String(comp.sale_price ?? ""));
  const [sqft, setSqft] = useState(String(comp.sqft ?? ""));
  const [distance, setDistance] = useState(String(comp.distance ?? ""));
  const [condition, setCondition] = useState(comp.condition || "similar");
  const [adjustment, setAdjustment] = useState(String(comp.adjustment ?? "0"));

  useEffect(() => {
    setAddr(comp.address);
    setPrice(String(comp.sale_price ?? ""));
    setSqft(String(comp.sqft ?? ""));
    setDistance(String(comp.distance ?? ""));
    setCondition(comp.condition || "similar");
    setAdjustment(String(comp.adjustment ?? "0"));
  }, [comp]);

  const commit = (field, value) => {
    if (String(comp[field] ?? "") === String(value ?? "")) return;
    onPatch({ [field]: value });
  };

  const ppsf = Number(comp.sale_price) / Math.max(1, Number(comp.sqft));
  const isLocked = !!comp.locked;
  const isExcluded = !!comp.excluded;
  const condMeta = CONDITION_META[condition] || CONDITION_META.similar;

  return (
    <div
      data-testid={`comp-row-${comp.id}`}
      className={[
        "grid grid-cols-12 gap-2 items-center px-4 py-2 transition-colors",
        isExcluded ? "opacity-50 bg-slate-50/40"
                   : (included ? "bg-emerald-50/40 hover:bg-emerald-50/60"
                               : "hover:bg-amber-50/40"),
      ].join(" ")}
    >
      <input
        type="text"
        value={addr}
        onChange={(e) => setAddr(e.target.value)}
        onBlur={() => commit("address", addr)}
        disabled={isLocked}
        data-testid={`comp-row-${comp.id}-address`}
        className="col-span-12 md:col-span-3 rounded-md border border-transparent hover:border-slate-200 focus:border-amber-400 bg-transparent px-2 py-1 text-[12.5px] focus:outline-none disabled:opacity-70"
      />
      <input
        type="number" min="0" value={price}
        onChange={(e) => setPrice(e.target.value)}
        onBlur={() => commit("sale_price", price)}
        disabled={isLocked}
        data-testid={`comp-row-${comp.id}-price`}
        className="col-span-4 md:col-span-2 rounded-md border border-transparent hover:border-slate-200 focus:border-amber-400 bg-transparent px-2 py-1 text-[12px] tabular-nums text-right focus:outline-none disabled:opacity-70"
      />
      <div className="col-span-2 md:col-span-1 text-right text-[11.5px] font-bold tabular-nums text-amber-700"
           data-testid={`comp-row-${comp.id}-ppsf`}>
        ${ppsf.toFixed(0)}
      </div>
      <input
        type="number" min="0" value={sqft}
        onChange={(e) => setSqft(e.target.value)}
        onBlur={() => commit("sqft", sqft ? Number(sqft) : null)}
        disabled={isLocked}
        data-testid={`comp-row-${comp.id}-sqft`}
        className="col-span-2 md:col-span-1 rounded-md border border-transparent hover:border-slate-200 focus:border-amber-400 bg-transparent px-2 py-1 text-[12px] tabular-nums text-right focus:outline-none disabled:opacity-70"
      />
      <input
        type="number" min="0" step="0.1" value={distance}
        onChange={(e) => setDistance(e.target.value)}
        onBlur={() => commit("distance", distance)}
        disabled={isLocked}
        data-testid={`comp-row-${comp.id}-distance`}
        className="col-span-2 md:col-span-1 rounded-md border border-transparent hover:border-slate-200 focus:border-amber-400 bg-transparent px-2 py-1 text-[12px] tabular-nums text-right focus:outline-none disabled:opacity-70"
      />
      <select value={condition}
              disabled={isLocked}
              onChange={(e) => { setCondition(e.target.value); onPatch({ condition: e.target.value }); }}
              data-testid={`comp-row-${comp.id}-condition`}
              className="col-span-4 md:col-span-1 rounded-md border border-transparent hover:border-slate-200 focus:border-amber-400 focus:outline-none bg-transparent px-2 py-1 text-[11px] font-bold uppercase disabled:opacity-70"
              style={{ color: condMeta.color }}>
        {Object.entries(CONDITION_META).map(([k, m]) => (
          <option key={k} value={k}>{m.label}</option>
        ))}
      </select>
      <input
        type="number" step="500" value={adjustment}
        onChange={(e) => setAdjustment(e.target.value)}
        onBlur={() => commit("adjustment", adjustment)}
        disabled={isLocked}
        data-testid={`comp-row-${comp.id}-adjustment`}
        className="col-span-4 md:col-span-2 rounded-md border border-transparent hover:border-slate-200 focus:border-amber-400 bg-transparent px-2 py-1 text-[12px] tabular-nums text-right focus:outline-none disabled:opacity-70"
      />

      {/* Actions */}
      <div className="col-span-4 md:col-span-1 flex items-center justify-end gap-1">
        <button
          type="button"
          onClick={() => onPatch({ locked: !isLocked })}
          data-testid={`comp-row-${comp.id}-lock`}
          title={isLocked ? "Unlock (allow edits)" : "Lock this comp"}
          className={`p-1 rounded transition-colors ${isLocked ? "text-amber-600 bg-amber-50" : "text-slate-400 hover:text-slate-700"}`}
        >
          {isLocked ? <Lock className="w-3.5 h-3.5" /> : <LockOpen className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => onPatch({ excluded: !isExcluded })}
          data-testid={`comp-row-${comp.id}-exclude`}
          title={isExcluded ? "Include in ARV" : "Exclude from ARV"}
          className={`p-1 rounded transition-colors ${isExcluded ? "text-rose-500 bg-rose-50" : "text-slate-400 hover:text-slate-700"}`}
        >
          {isExcluded ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid={`comp-row-${comp.id}-delete`}
          className="p-1 rounded text-slate-400 hover:text-rose-600 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────── Add Comp dialog ───────────

function AddCompDialog({ onSubmit, onClose }) {
  const [form, setForm] = useState(DEFAULT_NEW_COMP);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.address.trim() || !form.sale_price || !form.sqft) {
      toast.error("Address, sale price, and sqft are required.");
      return;
    }
    onSubmit({
      address: form.address.trim(),
      sale_price: String(form.sale_price),
      sale_date: form.sale_date,
      sqft: Number(form.sqft),
      distance: String(form.distance || "0"),
      beds: form.beds ? String(form.beds) : null,
      baths: form.baths ? String(form.baths) : null,
      condition: form.condition,
      adjustment: String(form.adjustment || "0"),
      notes: form.notes || null,
    });
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center" data-testid="comps-add-dialog">
      <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden animate-in fade-in-90 zoom-in-95"
      >
        <div className="h-1 w-full" style={{ backgroundColor: "#F59E0B" }} />
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Plus className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
              Add manual comp
            </div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">
              Comparable sale
            </div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="comps-add-dialog-close"
                  className="text-slate-400 hover:text-slate-600 text-lg leading-none">×</button>
        </div>
        <div className="p-5 space-y-3">
          <FormGroup label="Address *">
            <input type="text" required value={form.address} onChange={set("address")}
                   data-testid="comps-add-address"
                   className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </FormGroup>
          <div className="grid grid-cols-2 gap-3">
            <FormGroup label="Sale Price *">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input type="number" required value={form.sale_price} onChange={set("sale_price")}
                       data-testid="comps-add-price"
                       className="w-full rounded-lg border border-slate-200 bg-white pl-6 pr-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
            </FormGroup>
            <FormGroup label="Sale Date">
              <input type="date" value={form.sale_date} onChange={set("sale_date")}
                     data-testid="comps-add-date"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </FormGroup>
            <FormGroup label="Sqft *">
              <input type="number" required value={form.sqft} onChange={set("sqft")}
                     data-testid="comps-add-sqft"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </FormGroup>
            <FormGroup label="Distance (mi)">
              <input type="number" step="0.1" value={form.distance} onChange={set("distance")}
                     data-testid="comps-add-distance"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </FormGroup>
            <FormGroup label="Beds">
              <input type="number" step="0.5" value={form.beds} onChange={set("beds")}
                     data-testid="comps-add-beds"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </FormGroup>
            <FormGroup label="Baths">
              <input type="number" step="0.5" value={form.baths} onChange={set("baths")}
                     data-testid="comps-add-baths"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </FormGroup>
            <FormGroup label="Condition">
              <select value={form.condition} onChange={set("condition")}
                      data-testid="comps-add-condition"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                {Object.entries(CONDITION_META).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
            </FormGroup>
            <FormGroup label="Adjustment ($)">
              <input type="number" step="500" value={form.adjustment} onChange={set("adjustment")}
                     data-testid="comps-add-adjustment"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </FormGroup>
          </div>
        </div>
        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[13px] font-semibold px-3 py-2">
            Cancel
          </button>
          <button type="submit"
                  data-testid="comps-add-submit"
                  className="rounded-lg text-white text-[13px] font-semibold px-4 py-2 shadow-md"
                  style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            <Save className="w-4 h-4 inline mr-1" /> Add comp
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────── Search Drawer ─────────────

function SearchDrawer({ searching, onSubmit, onClose }) {
  const [radius, setRadius] = useState("0.5");
  const [monthsBack, setMonthsBack] = useState("12");
  const [sqftTol, setSqftTol] = useState("0.25");
  const [bedsTol, setBedsTol] = useState("1");
  const [bathsTol, setBathsTol] = useState("1");
  const [excludeDistressed, setExcludeDistressed] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      radius_miles: radius,
      months_back: Number(monthsBack),
      sqft_tolerance_pct: sqftTol,
      beds_tolerance: Number(bedsTol),
      baths_tolerance: bathsTol,
      exclude_distressed: excludeDistressed,
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end"
         data-testid="comps-search-drawer">
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit}
            className="relative h-full w-full max-w-[420px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Search className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">Auto-search</div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">Comparable filters</div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="comps-search-drawer-close"
                  className="text-slate-400 hover:text-slate-600 text-lg leading-none">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <FormGroup label="Radius (miles)">
            <input type="number" min="0.1" max="10" step="0.1" value={radius}
                   onChange={(e) => setRadius(e.target.value)}
                   data-testid="comps-search-radius"
                   className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </FormGroup>
          <FormGroup label="Months back">
            <input type="number" min="1" max="60" value={monthsBack}
                   onChange={(e) => setMonthsBack(e.target.value)}
                   data-testid="comps-search-months"
                   className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </FormGroup>
          <FormGroup label="Sqft tolerance (± %)">
            <input type="number" min="0" max="1" step="0.05" value={sqftTol}
                   onChange={(e) => setSqftTol(e.target.value)}
                   data-testid="comps-search-sqft-tol"
                   className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </FormGroup>
          <div className="grid grid-cols-2 gap-3">
            <FormGroup label="Beds tolerance">
              <input type="number" min="0" max="5" value={bedsTol}
                     onChange={(e) => setBedsTol(e.target.value)}
                     data-testid="comps-search-beds-tol"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </FormGroup>
            <FormGroup label="Baths tolerance">
              <input type="number" min="0" max="5" step="0.5" value={bathsTol}
                     onChange={(e) => setBathsTol(e.target.value)}
                     data-testid="comps-search-baths-tol"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </FormGroup>
          </div>
          <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
            <input type="checkbox" checked={excludeDistressed}
                   onChange={(e) => setExcludeDistressed(e.target.checked)}
                   data-testid="comps-search-exclude-distressed"
                   className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-400" />
            Exclude distressed sales (REO / short-sale)
          </label>
        </div>
        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[13px] font-semibold px-3 py-2">
            Cancel
          </button>
          <button type="submit"
                  disabled={searching}
                  data-testid="comps-search-submit"
                  className="rounded-lg text-white text-[13px] font-semibold px-4 py-2 shadow-md disabled:opacity-60"
                  style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            {searching ? <Loader2 className="w-4 h-4 inline mr-1 animate-spin" /> : <Search className="w-4 h-4 inline mr-1" />}
            Run search
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────── property picker ──────────

function PropertyPicker({ properties, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const selected = properties.find((p) => p.id === value);
  const filtered = useMemo(() => {
    if (!q.trim()) return properties;
    const t = q.trim().toLowerCase();
    return properties.filter((p) => `${p.address?.street || ""} ${p.address?.city || ""}`.toLowerCase().includes(t));
  }, [properties, q]);
  return (
    <div ref={boxRef} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
              disabled={loading || properties.length === 0}
              data-testid="comps-property-picker"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white hover:border-amber-400 px-3 py-2 text-[13px] font-semibold text-slate-800 shadow-sm min-w-[240px] justify-between disabled:opacity-60">
        <span className="truncate text-left">
          {loading ? "Loading…" : selected ? `${selected.address?.street || "Property"} · ${selected.address?.state || ""}` : "Select property"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div data-testid="comps-property-picker-menu"
             className="absolute right-0 top-full mt-1 w-[320px] max-h-[360px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl z-30 flex flex-col">
          <div className="p-2 border-b border-slate-100">
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search address, city…" autoFocus
                   className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-[12px] text-slate-500 text-center">No matches.</div>
            ) : filtered.map((p) => (
              <button key={p.id} type="button"
                      data-testid={`comps-property-picker-opt-${p.id}`}
                      onClick={() => { onChange(p.id); setOpen(false); setQ(""); }}
                      className={`w-full text-left px-3 py-2 text-[12.5px] hover:bg-amber-50 border-b border-slate-50 ${p.id === value ? "bg-amber-50/60" : ""}`}>
                <div className="font-semibold text-slate-900 truncate">{p.address?.street || "—"}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {[p.address?.city, p.address?.state, p.address?.zip_code].filter(Boolean).join(", ")}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── small helpers ────────────

function FormGroup({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
function KpiTile({ label, value, color, testid }) {
  return (
    <div className="rounded-lg bg-slate-50/70 border border-slate-100 px-3 py-2"
         data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-slate-500">
        <span className="h-1 w-1 rounded-full" style={{ backgroundColor: color }} />
        {label}
      </div>
      <div className="mt-0.5 text-[13px] font-bold text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function AiList({ label, items, color, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color }}>
        {label}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-[11.5px] text-slate-700 flex items-start gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: color }} />
            <span>{typeof it === "string" ? it : it.address || it.label || JSON.stringify(it)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
